import React, { ReactNode, useContext, useMemo, useState } from 'react';
import {
  BrukerrollerDto,
  RolleDto,
  StudiestedbrukerDto,
  StudiestedRollerDto,
} from '../../api/generated/studiested-generated-api';
import { ConfirmationDialog } from '../../components/dialog';
import * as css from '../rolleliste.module.scss';
import translations from '../rolleliste.i18n';
import { useFormatMessageFor } from '../../i18n/translation';
import Button from '../../components/button';
import { FakeLockedInput } from '../../components/input';
import { UhaUserContext } from '../../user/UhaUserStore';
import RollelisteStudiestedbrukerDropDown from './rollelisteStudiestedbrukerDropDown';

interface RollelisteStudiestedbrukerDialogProps {
  id: string;
  rollerData: RolleDto[] | undefined;
  title?: ReactNode;
  titleValue: string | undefined;
  onClose?: (
    ok: boolean,
    selectedItem?: StudiestedbrukerDto,
    rolleTypeStudiested?: StudiestedRollerDto,
  ) => void;
}

const RollelisteStudiestedbrukerDialog = ({
  id,
  rollerData,
  title,
  titleValue,
  onClose,
}: RollelisteStudiestedbrukerDialogProps) => {
  const labels = useFormatMessageFor(translations);

  const [modalVisible, setModalVisible] = useState(false);
  const [rolleTypeStudiested, setRolleTypeStudiested] =
    useState<StudiestedRollerDto>();
  const [selectedStudiestedbruker, setSelectedStudiestedbruker] =
    useState<StudiestedbrukerDto>();

  const { uhaUser: bruker } = useContext(UhaUserContext);

  const canWrite = useMemo(
    () => bruker && bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet,
    [bruker],
  );

  const addModalButtons = (
    <div className={css.roleGroupButtonContainer}>
      {canWrite && (
        <>
          <Button.Secondary
            key={`add-ss-koordinator-${id}`}
            onClick={() => {
              setRolleTypeStudiested(StudiestedRollerDto.Koordinator);
              setModalVisible(true);
            }}
          >
            {labels.addUserKoordinator}
          </Button.Secondary>
          <Button.Secondary
            key={`add-ss-underviser-${id}`}
            onClick={() => {
              setRolleTypeStudiested(StudiestedRollerDto.Underviser);
              setModalVisible(true);
            }}
          >
            {labels.addUserUnderviser}
          </Button.Secondary>
        </>
      )}
    </div>
  );

  const modalTitle = useMemo(() => {
    switch (rolleTypeStudiested) {
      case StudiestedRollerDto.Koordinator:
        return labels.modalHeaderKoordinator;
      case StudiestedRollerDto.Underviser:
        return labels.modalHeaderUnderviser;
      default:
        return '';
    }
  }, [
    rolleTypeStudiested,
    labels.modalHeaderKoordinator,
    labels.modalHeaderUnderviser,
  ]);

  return (
    <>
      <ConfirmationDialog
        key={`confirmation-dialog-${id}`}
        extraClasses={css.rolleListeModal}
        contentCss={css.rolleListeContent}
        title={modalTitle}
        subtitle={title}
        isOpen={modalVisible}
        submitLabel={labels.confirm}
        cancelLabel={labels.cancel}
        submitDisabled={!selectedStudiestedbruker}
        onClose={(ok: boolean) => {
          setModalVisible(false);
          onClose && onClose(ok, selectedStudiestedbruker, rolleTypeStudiested);
        }}
      >
        <FakeLockedInput
          id={`locked-input-${id}`}
          label={labels.access}
          value={titleValue}
        />
        <RollelisteStudiestedbrukerDropDown
          id={`studiessted-profil-${id}`}
          rollerData={rollerData}
          rolleTypeStudiested={rolleTypeStudiested}
          onChange={(value) => {
            setSelectedStudiestedbruker(value);
          }}
        />
      </ConfirmationDialog>

      <div className={css.addModalButtons}>{addModalButtons}</div>
    </>
  );
};

export default RollelisteStudiestedbrukerDialog;
